/** Nx / Turborepo-style nested workspace folders. */
const NESTED_WORKSPACE = new Set([
  'apps',
  'packages',
  'services',
  'libs',
  'modules',
  'projects',
]);

/** Sibling roots that mark a polyglot / split-stack monorepo. */
const SIBLING_ROOTS = new Set([
  'backend',
  'webclient',
  'frontend',
  'client',
  'server',
  'api',
  'mobile',
  'web',
]);

const LANGUAGE_ONLY_ROOTS = new Set([
  'src',
  'lib',
  'source',
  'app',
  'test',
  'tests',
  'e2e',
  'scripts',
  'tools',
  'docs',
  'public',
]);

export type InventoryLanguage =
  | 'typescript'
  | 'javascript'
  | 'csharp'
  | 'python'
  | 'java'
  | 'mixed';

export interface InventoryWorkspaceFile {
  filePath?: string;
  fileName?: string;
}

export interface InventoryProject<T extends InventoryWorkspaceFile> {
  id: string;
  label: string;
  files: T[];
  language: InventoryLanguage;
}

function normalizePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+/, '');
}

function extensionOf(file: InventoryWorkspaceFile): string {
  const name = (file.filePath || file.fileName || '').toLowerCase();
  const base = name.split('/').pop() || name;
  const dot = base.lastIndexOf('.');
  return dot >= 0 ? base.slice(dot) : '';
}

export function inferLanguage(
  files: InventoryWorkspaceFile[],
): InventoryLanguage {
  let typescript = 0;
  let javascript = 0;
  let csharp = 0;
  let python = 0;
  let java = 0;
  let tsx = 0;
  for (const file of files) {
    const ext = extensionOf(file);
    if (ext === '.tsx' || ext === '.ts' || ext === '.mts') {
      typescript += 1;
      if (ext === '.tsx') tsx += 1;
    } else if (ext === '.jsx' || ext === '.js' || ext === '.mjs' || ext === '.cjs') {
      javascript += 1;
    } else if (ext === '.cs') csharp += 1;
    else if (ext === '.py') python += 1;
    else if (ext === '.java') java += 1;
  }
  const ranked: [InventoryLanguage, number][] = [
    ['typescript', typescript],
    ['javascript', javascript],
    ['csharp', csharp],
    ['python', python],
    ['java', java],
  ];
  ranked.sort((a, b) => b[1] - a[1]);
  if (ranked[0][1] === 0) return 'mixed';
  if (ranked[1][1] > 0 && ranked[1][1] >= ranked[0][1] * 0.4) return 'mixed';
  return ranked[0][0];
}

export function languageCaption(language: InventoryLanguage, files: InventoryWorkspaceFile[]): string {
  if (language === 'csharp') return '.NET · C#';
  if (language === 'python') return 'Python';
  if (language === 'java') return 'Java';
  if (language === 'javascript') return 'JavaScript';
  if (language === 'typescript') {
    const hasTsx = files.some(f => extensionOf(f) === '.tsx');
    return hasTsx ? 'React · TypeScript' : 'TypeScript';
  }
  return 'Mixed';
}

function looksLikeFileName(segment: string): boolean {
  return segment.includes('.') && !segment.startsWith('.');
}

export function projectKeyForPath(path: string): string {
  const parts = normalizePath(path).split('/').filter(Boolean);
  if (parts.length === 0) return '.';
  const first = parts[0];
  if (looksLikeFileName(first)) return '.';
  const firstLower = first.toLowerCase();
  if (NESTED_WORKSPACE.has(firstLower) && parts.length >= 2 && !looksLikeFileName(parts[1])) {
    return `${parts[0]}/${parts[1]}`;
  }
  if (NESTED_WORKSPACE.has(firstLower)) return '.';
  return parts[0];
}

/** Path relative to a workspace project root (`apps/web/src/a.ts` → `src/a.ts`). */
export function stripProjectPrefix(path: string, projectId: string): string {
  const normalized = normalizePath(path);
  if (projectId === '.' || !projectId) return normalized;
  const prefix = `${normalizePath(projectId)}/`;
  return normalized.startsWith(prefix)
    ? normalized.slice(prefix.length)
    : normalized;
}

function looksLikeMonorepo(keys: string[]): boolean {
  const packageKeys = keys.filter(key => key !== '.');
  if (
    packageKeys.some(key => {
      const root = key.split('/')[0].toLowerCase();
      return NESTED_WORKSPACE.has(root) || SIBLING_ROOTS.has(root);
    })
  ) {
    return true;
  }
  return (
    packageKeys.length >= 2 &&
    !packageKeys.every(key =>
      LANGUAGE_ONLY_ROOTS.has(key.split('/')[0].toLowerCase()),
    )
  );
}

/**
 * Group scanned files into workspace projects.
 * Single-package repos collapse to one project named after the repository.
 */
export function groupInventoryProjects<T extends InventoryWorkspaceFile>(
  files: T[],
  repoName: string,
): InventoryProject<T>[] {
  const buckets = new Map<string, T[]>();
  for (const file of files) {
    const key = projectKeyForPath(file.filePath || file.fileName || '');
    const list = buckets.get(key);
    if (list) list.push(file);
    else buckets.set(key, [file]);
  }

  const repoLabel = repoName.trim() || 'Repository';
  const keys = [...buckets.keys()];
  if (!looksLikeMonorepo(keys)) {
    return [
      {
        id: repoLabel,
        label: repoLabel,
        files,
        language: inferLanguage(files),
      },
    ];
  }

  return keys
    .sort((a, b) => a.localeCompare(b))
    .map(id => {
      const projectFiles = buckets.get(id) ?? [];
      return {
        id,
        label: id === '.' ? repoLabel : id,
        files: projectFiles,
        language: inferLanguage(projectFiles),
      };
    });
}
